import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { CreateContactUsPublicRequest, postContactsUsCreate } from "api"
import "dayjs/locale/he"
import { useTranslations } from "next-intl"
import { Dispatch, LegacyRef, SetStateAction, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useForm } from "react-hook-form"
import { MarketingConsent } from "ui"
import {
  applicationRequestError,
  applicationRequestSuccess,
  useFocusOnError,
} from "utils"
import { commonSchema, messagesInvalid } from "utils/FormSchema"
import * as yup from "yup"
import { FormId } from "../MainForm"
import {
  CustomTextField,
  DateController,
  Header,
  MultilineCustomTextField,
  NumbersController,
  RECAPTCHA_KEY,
  formatDate,
  isFieldRequired,
  notAfterNowIfToday,
  prefixOptionsArray,
} from "./Common"

import { FormContainer, InputsGrid, StyledButton } from "./styles"

export default function Ravkav({
  handleResult,
  locale,
  contactType,
  setIsLoading,
  label,
  tooltipText,
}: {
  handleResult: (result: "yes" | "no", id?: string) => void
  locale: "he" | "en" | "ar" | "ru"
  contactType: FormId
  setIsLoading: Dispatch<SetStateAction<boolean>>
  label: string
  tooltipText: string
}) {
  const [isPending, setIsPending] = useState(false)
  const t = useTranslations("Forms")
  const tInner = useTranslations("InnerPages")
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const schema = yup.object({
    firstName: commonSchema.common(locale).firstName,
    lastName: commonSchema.common(locale).lastName,
    customerId: commonSchema.common(locale).customerId.optional(),
    ravKavAccount: commonSchema
      .common(locale)
      .ravkav.required(messagesInvalid[locale].ravkavRequired),
    email: commonSchema.common(locale).email,
    cellphone: commonSchema.common(locale).phonePrefix,
    phone: commonSchema.common(locale).phone,
    eventDate: commonSchema.common(locale).eventDate,
    lineNum: commonSchema.common(locale).lineNumber,
    tripDestination: commonSchema.common(locale).tripDirection,
    time: commonSchema
      .common(locale)
      .exactEventTime.test(
        "notAfterNowIfToday",
        messagesInvalid[locale].timeNotInFuture,
        function (value) {
          const { eventDate } = this.parent
          return notAfterNowIfToday(value, eventDate)
        },
      ),
    dropDestination: commonSchema
      .common(locale)
      .dropOffDestination.required(messagesInvalid[locale].dropOffDestination),
    location: commonSchema.common(locale).eventLocation,
    incidentArea: commonSchema.common(locale).incidentArea,
    content: commonSchema.common(locale).inquiryDescription,
    mailingConsent: yup.boolean().optional(),
  })
  type FormData = yup.InferType<typeof schema>

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      cellphone: prefixOptionsArray[0].value,
    },
  })

  // Add focus management for validation errors
  useFocusOnError(errors, isSubmitted)

  const { mutate } = useMutation({
    mutationFn: postContactsUsCreate,
    onMutate: () => {
      setIsLoading(true)
    },
    onSuccess: (data) => {
      if (data.data.errorCode) {
        applicationRequestError(tInner("submitInquiry"), label)
        handleResult("yes")
      } else {
        applicationRequestSuccess(tInner("submitInquiry"), label)
        handleResult("no", data.data.body.caseNumber)
      }
    },
    onError: () => {
      applicationRequestError(tInner("submitInquiry"), label)
      handleResult("yes")
    },
    onSettled: () => {
      setIsLoading(false)
      setIsPending(false)
    },
  })

  const onSubmit = async (data: FormData) => {
    setIsPending(true)
    // Combine date and time into a single ISO string
    const eventDate = formatDate(data.eventDate, data.time)
    reCaptchaRef?.current?.reset()
    const recaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string

    const formattedData = {
      ...data,
      eventDate,
      contactTypeId: contactType,
      recaptchaResponse,
      sid: window?.__ctm?.config?.sid || "-1",
    }

    mutate(formattedData as unknown as CreateContactUsPublicRequest)
  }

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <Header />
      <InputsGrid>
        <CustomTextField
          name="firstName"
          control={control}
          label={t("firstName")}
          required={isFieldRequired(schema, "firstName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="lastName"
          control={control}
          label={t("lastName")}
          required={isFieldRequired(schema, "lastName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="email"
          control={control}
          label={t("email")}
          required={isFieldRequired(schema, "email")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <NumbersController
          prefixName="cellphone"
          phoneName="phone"
          control={control}
          label={t("mobileNumber")}
          required={isFieldRequired(schema, "phone")}
          isSubmitted={isSubmitted}
          errors={errors}
          locale={locale}
        />
        <CustomTextField
          name="customerId"
          control={control}
          label={t("idCard")}
          required={isFieldRequired(schema, "customerId")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
      </InputsGrid>

      <Typography variant="h4">{tInner("moreDetails")}</Typography>
      <InputsGrid>
        <CustomTextField
          name="ravKavAccount"
          control={control}
          label={t("ravKav")}
          required={isFieldRequired(schema, "ravKavAccount")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="lineNum"
          control={control}
          label={t("lineNumber")}
          required={isFieldRequired(schema, "lineNum")}
          isSubmitted={isSubmitted}
          errors={errors}
          maxLength={5}
        />
        <DateController
          name="eventDate"
          control={control}
          label={t("eventDate")}
          required={true}
          isSubmitted={isSubmitted}
          errors={errors}
          midnightText={tInner("afterMidnight")}
          okButtonLabel={tInner("okButtonLabel")}
          cancelButtonLabel={tInner("cancelButtonLabel")}
          toolbarTitle={tInner("toolbarTitle")}
          locale={locale}
        />
        <CustomTextField
          name="time"
          control={control}
          label={t("exactEventTime")}
          required={isFieldRequired(schema, "time")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="tripDestination"
          control={control}
          label={t("tripDirection")}
          required={isFieldRequired(schema, "tripDestination")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="dropDestination"
          control={control}
          label={t("dropOffStation")}
          required={isFieldRequired(schema, "dropDestination")}
          isSubmitted={isSubmitted}
          errors={errors}
        />

        <CustomTextField
          name="incidentArea"
          control={control}
          label={t("settlement")}
          required={isFieldRequired(schema, "incidentArea")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="location"
          control={control}
          label={t("eventLocation")}
          required={isFieldRequired(schema, "location")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
      </InputsGrid>
      <MultilineCustomTextField
        name="content"
        control={control}
        label={t("inquiryDescription")}
        required={isFieldRequired(schema, "content")}
        isSubmitted={isSubmitted}
        errors={errors}
      />
      <MarketingConsent
        name="mailingConsent"
        control={control}
        tooltipText={tooltipText}
      />
      <StyledButton variant="primary" type="submit" disabled={isPending}>
        {tInner("submitInquiry")}
      </StyledButton>
      {RECAPTCHA_KEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA>}
          sitekey={RECAPTCHA_KEY}
          size="invisible"
          hl="iw"
          tabIndex={-1}
        />
      )}
    </FormContainer>
  )
}
